import React, {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import styled, { keyframes } from "styled-components";
import { Modal } from "@material-ui/core";
import { useSnackbar } from "notistack";
import * as XLSX from "xlsx";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCTAButton,
} from "../../common/Buttons";
import SearchBar from "../../common/SearchBar/SearchBar";

const datatypeLegend = [
  {
    type: "COLUMN HEADER (any error)",
    color: "#ff0000",
    meaning: "This column has at least one bad value below",
    example: "-",
    count: "-",
  },
  {
    type: "date",
    color: "#4f81bd",
    meaning:
      "Cell should be a valid date, or a day of the month written 1st to 31st",
    example:
      "e.g. 18-06-2026, 22-Jun-2026, 22/Jun/2026, or a day alone: 1st, 2nd, 3rd ... 31st",
  },
  {
    type: "time",
    color: "#8064a2",
    meaning: "Cell should be a valid clock time (AM/PM is case-sensitive)",
    example: "e.g. 11:00 AM to 11:30 AM, 1:00 PM to 4:30 PM",
  },
  {
    type: "int",
    color: "#ff8000",
    meaning: "Cell should be a whole number",
    example: "e.g. 40 (whole number, no decimal point)",
  },
  {
    type: "float",
    color: "#8c564b",
    meaning: "Cell should be an amount, with or without a decimal point",
    example: "e.g. 40 or 40.50 (with or without decimal point)",
  },
  {
    type: "name",
    color: "#4bacc6",
    meaning: "Cell should be a name (letters, spaces, and periods only)",
    example:
      "e.g. Ramesh Kumar / K. Ramesh (letters, spaces, and periods only)",
  },
  {
    type: "phone",
    color: "#da70d6",
    meaning:
      "Cell should be a digits-only phone number (with or without country code)",
    example: "e.g. 9876543210, 919876543210",
  },
  {
    type: "age",
    color: "#2ca02c",
    meaning: "Cell should be a whole age 0-130",
    example: "40 (whole number, 0-130)",
  },
  {
    type: "words",
    color: "#BCBD22",
    meaning:
      "Cell should contain letters, spaces, and hyphens only ('in words' field)",
    example: "e.g. Two Lakh Fifty Thousand Only / Two-Lakh Fifty Thousand Only",
  },
  {
    type: "string",
    color: "#BCBD22",
    meaning: "Cell can contain any text — letters, digits, spaces, and symbols",
    example:
      "e.g. JustAct Case Manager, https://zoom.us/j/92111..., 6/90 Ground Floor, Chennai-600018",
  },
];

const DATATYPE_HINTS = {
  int: "Whole number",
  float: "Amount",
  date: "Date",
  time: "Clock time",
  name: "Letters only",
  phone: "Digits only",
  age: "0 to 130",
  words: "In words",
  string: "Any text",
};

const DATATYPE_OPTIONS = [
  "int",
  "float",
  "date",
  "time",
  "name",
  "phone",
  "age",
  "words",
  "string",
];

const tipFromLegend = (item) => ({
  type: item.type,
  color: item.color,
  meaning: item.meaning,
  example: String(item.example || "").replace(/^e\.g\.\s*/i, ""),
});

const datatypeSelectOptions = DATATYPE_OPTIONS.map((type) => {
  const item = datatypeLegend.find((l) => l.type === type);
  return {
    value: type,
    label: type,
    hint: DATATYPE_HINTS[type],
    color: item?.color,
    tip: item ? tipFromLegend(item) : null,
  };
});

const reviewSteps = [
  {
    step: 1,
    title: "Header missing in template",
    short: "Missing",
    help: "These template placeholders have no matching column in your Excel. Pick the right Excel header for each one.",
  },
  {
    step: 2,
    title: "Duplicate Excel headers",
    short: "Duplicates",
    help: "The same header should not appear twice in your Excel. If any are listed, fix the file and upload it again.",
  },
  {
    step: 3,
    title: "Headers matched",
    short: "Matched",
    report: "headers_ok",
    help: "These template placeholders were matched with your Excel headers.",
  },
  {
    step: 4,
    title: "Data types",
    short: "Data types",
    report: "datatype_check",
    help: "Choose what kind of value each column holds. Hover a type to see what it means and a valid example.",
  },
  {
    step: 5,
    title: "Empty values",
    short: "Empty values",
    report: "null_values",
    help: "Columns and rows that have empty cells. Check these before you confirm.",
  },
];

const clamp = (n, min, max) => Math.max(min, Math.min(n, max));

const TipContent = ({ tip }) => (
  <>
    <TipHead>
      <TipDot tone={tip.color} />
      <TipType>{tip.type}</TipType>
    </TipHead>
    <TipText>{tip.meaning}</TipText>
    <TipLabel>Valid example</TipLabel>
    <TipExample>{tip.example}</TipExample>
  </>
);

const FloatingTip = ({ anchorEl, prefer = "right", tip }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!anchorEl || !ref.current) return;
    const r = anchorEl.getBoundingClientRect();
    const tw = ref.current.offsetWidth;
    const th = ref.current.offsetHeight;
    const gap = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let side = prefer;

    if (side === "right" && r.right + gap + tw > vw - 8) {
      side = r.left - gap - tw > 8 ? "left" : "top";
    } else if (side === "left" && r.left - gap - tw < 8) {
      side = r.right + gap + tw <= vw - 8 ? "right" : "top";
    }
    if (side === "top" && r.top - gap - th < 8) side = "bottom";

    let top;
    let left;
    let arrow;
    if (side === "right" || side === "left") {
      left = side === "right" ? r.right + gap : r.left - gap - tw;
      top = clamp(r.top + r.height / 2 - th / 2, 8, Math.max(8, vh - th - 8));
      arrow = r.top + r.height / 2 - top;
    } else {
      top = side === "top" ? r.top - gap - th : r.bottom + gap;
      left = clamp(r.left + r.width / 2 - tw / 2, 8, Math.max(8, vw - tw - 8));
      arrow = r.left + r.width / 2 - left;
    }
    setPos({ top, left, side, arrow });
  }, [anchorEl, prefer, tip]);

  if (!anchorEl || !tip) return null;

  const arrowStyle = {
    top: "auto",
    bottom: "auto",
    left: "auto",
    right: "auto",
  };
  if (pos) {
    const a = clamp(pos.arrow, 14, 9999) - 5;
    if (pos.side === "right") {
      arrowStyle.left = -6;
      arrowStyle.top = a;
      arrowStyle.borderLeft = "1px solid #e5e7eb";
      arrowStyle.borderBottom = "1px solid #e5e7eb";
    } else if (pos.side === "left") {
      arrowStyle.right = -6;
      arrowStyle.top = a;
      arrowStyle.borderTop = "1px solid #e5e7eb";
      arrowStyle.borderRight = "1px solid #e5e7eb";
    } else if (pos.side === "top") {
      arrowStyle.bottom = -6;
      arrowStyle.left = a;
      arrowStyle.borderRight = "1px solid #e5e7eb";
      arrowStyle.borderBottom = "1px solid #e5e7eb";
    } else {
      arrowStyle.top = -6;
      arrowStyle.left = a;
      arrowStyle.borderLeft = "1px solid #e5e7eb";
      arrowStyle.borderTop = "1px solid #e5e7eb";
    }
  }

  return createPortal(
    <TipBox
      ref={ref}
      role="tooltip"
      style={{
        top: pos ? pos.top : 0,
        left: pos ? pos.left : 0,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      <TipArrow style={arrowStyle} />
      <TipContent tip={tip} />
    </TipBox>,
    document.body,
  );
};

const ModernSelect = ({
  value,
  onChange,
  options,
  placeholder = "Choose",
  searchable = false,
  name,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuPos, setMenuPos] = useState(null);
  const [tipState, setTipState] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const listRef = useRef(null);

  const selected = options.find((o) => o.value === value);
  const q = query.trim().toLowerCase();
  const visible = q
    ? options.filter(
        (o) => o.isGroup || String(o.label).toLowerCase().includes(q),
      )
    : options;

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(Math.max(r.width, 260), vw - 16);
    const left = clamp(r.left, 8, vw - width - 8);
    const menuH = menuRef.current ? menuRef.current.offsetHeight : 300;
    const below = vh - r.bottom - 14;
    const above = r.top - 14;
    const up = below < Math.min(menuH, 260) && above > below;
    setMenuPos({
      left,
      width,
      top: up ? undefined : r.bottom + 6,
      bottom: up ? vh - r.top + 6 : undefined,
      maxHeight: Math.max(160, Math.min(up ? above : below, 380)),
    });
  }, []);

  useLayoutEffect(() => {
    if (open) updatePos();
  }, [open, visible.length, updatePos]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onMove = (e) => {
      if (e?.target && menuRef.current && menuRef.current.contains(e.target))
        return;
      updatePos();
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, updatePos]);

  useLayoutEffect(() => {
    if (!open || activeIndex < 0) {
      setTipState(null);
      return;
    }
    const opt = visible[activeIndex];
    const el = listRef.current?.children[activeIndex];
    if (el) el.scrollIntoView({ block: "nearest" });
    setTipState(opt?.tip && el ? { el, tip: opt.tip } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeIndex, query]);

  const openMenu = () => {
    setQuery("");
    setActiveIndex(options.findIndex((o) => o.value === value));
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
    setTipState(null);
  };

  const choose = (opt) => {
    if (opt.disabled) return;
    onChange(opt.value);
    closeMenu();
    if (triggerRef.current) triggerRef.current.focus();
  };

  const handleKey = (e) => {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (visible.length ? (i + 1) % visible.length : -1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) =>
        visible.length ? (i <= 0 ? visible.length - 1 : i - 1) : -1,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && visible[activeIndex])
        choose(visible[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closeMenu();
      if (triggerRef.current) triggerRef.current.focus();
    } else if (e.key === "Tab") {
      closeMenu();
    }
  };

  return (
    <SelectRoot>
      <SelectTrigger
        ref={triggerRef}
        type="button"
        name={name}
        aria-haspopup="listbox"
        aria-expanded={open}
        isOpen={open}
        isFilled={Boolean(selected)}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={handleKey}
      >
        {selected?.color && <TipDot tone={selected.color} />}
        <TriggerText isPlaceholder={!selected}>
          {selected ? selected.label : placeholder}
        </TriggerText>
        <Chevron isOpen={open}>▾</Chevron>
      </SelectTrigger>

      {open &&
        createPortal(
          <MenuPanel
            ref={menuRef}
            style={
              menuPos
                ? {
                    left: menuPos.left,
                    width: menuPos.width,
                    top: menuPos.top,
                    bottom: menuPos.bottom,
                    maxHeight: menuPos.maxHeight,
                  }
                : { left: 0, top: 0, visibility: "hidden" }
            }
          >
            {searchable && (
              <MenuSearch>
                <MenuSearchIcon aria-hidden="true">⌕</MenuSearchIcon>
                <MenuSearchInput
                  autoFocus
                  value={query}
                  placeholder="Search"
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActiveIndex(0);
                  }}
                  onKeyDown={handleKey}
                />
              </MenuSearch>
            )}
            <MenuList
              ref={listRef}
              role="listbox"
              onMouseLeave={() => setActiveIndex(-1)}
            >
              {visible.length === 0 ? (
                <MenuEmpty>No matches found</MenuEmpty>
              ) : (
                visible.map((opt, i) => (
                  <MenuOption
                    key={String(opt.value)}
                    role="option"
                    aria-selected={opt.value === value}
                    isActive={i === activeIndex}
                    isSelected={opt.value === value}
                    onMouseEnter={() => !opt.disabled && setActiveIndex(i)}
                    onClick={() => choose(opt)}
                  >
                    {opt.isGroup ? (
                      <OptionHint>{opt.label}</OptionHint>
                    ) : (
                      <>
                        {opt.color && <TipDot tone={opt.color} />}
                        <OptionLabel>{opt.label}</OptionLabel>
                        {opt.hint && <OptionHint>{opt.hint}</OptionHint>}
                      </>
                    )}
                    {!opt.isGroup && opt.value === value && (
                      <OptionCheck aria-hidden="true">✓</OptionCheck>
                    )}
                  </MenuOption>
                ))
              )}
            </MenuList>
          </MenuPanel>,
          document.body,
        )}

      {open && tipState && (
        <FloatingTip anchorEl={tipState.el} prefer="right" tip={tipState.tip} />
      )}
    </SelectRoot>
  );
};

const ResponseModal = ({
  open,
  onClose,
  responseData,
  title,
  datatypeErrors,
  errorExcel,
  previewExcel,
  bulkReview,
  onReviewConfirm,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [reviewStep, setReviewStep] = useState(0);
  const [reportData, setReportData] = useState({});
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [headerSelections, setHeaderSelections] = useState({});
  const [datatypeSelections, setDatatypeSelections] = useState({});
  const [excelHeaderSearch, setExcelHeaderSearch] = useState("");
  const [showGuide, setShowGuide] = useState(true);
  const [emptyRowsFilter, setEmptyRowsFilter] = useState([
    "allRowsEmpty",
    "nullRows",
  ]);
  const [previewErrorCounts, setPreviewErrorCounts] = useState({});

  const datatypeErrorCount = (type) =>
    Object.prototype.hasOwnProperty.call(previewErrorCounts, type)
      ? previewErrorCounts[type]
      : (datatypeErrors || []).filter((error) => error.expected_type === type)
          .length;

  const downloadErrorExcel = () => {
    const link = document.createElement("a");
    link.href = errorExcel;
    link.download = "datatype_errors.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyTable = () => {
    if (!responseData?.declinedCases?.length) return;
    const rows = responseData.declinedCases
      .map((c) => `${c.caseId}\t${c.resolutionKind}\t${c.reason}\t${c.status}`)
      .join("\n");
    navigator.clipboard.writeText(
      "Case ID\tResolution Kind\tReason\tStatus\n" + rows,
    );
    enqueueSnackbar("Table copied to clipboard!", { variant: "success" });
  };

  const activeReviewStep = reviewSteps[reviewStep];
  const activeReport = activeReviewStep?.report;
  const isCurrentReportPending =
    Boolean(activeReport) && !reportData[activeReport];
  const duplicateHeaders = bulkReview?.review?.duplicateHeaders || [];
  const excelHeaders = bulkReview?.review?.only_excelHeader || [];
  const headerSuggestions = bulkReview?.review?.headerSuggestions || [];

  useEffect(() => {
    setReviewStep(0);
    setReportData({});
    setHeaderSelections({});
    setDatatypeSelections({});
    setExcelHeaderSearch("");
    setShowGuide(true);
    setEmptyRowsFilter(["allRowsEmpty", "nullRows"]);
  }, [bulkReview]);

  useEffect(() => {
    if (!previewExcel) {
      setPreviewErrorCounts({});
      return;
    }

    const getPreviewErrorCounts = async () => {
      try {
        const response = await fetch(previewExcel);
        const workbook = XLSX.read(await response.arrayBuffer(), {
          type: "array",
        });
        const counts = {};

        workbook.SheetNames.forEach((sheetName) => {
          const sheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: "",
          });
          const headerRowIndex = rows.findIndex(
            (row) =>
              row.some((cell) => /expected\s*datatype/i.test(String(cell))) &&
              row.some((cell) => /no\.\s*of\s*errors/i.test(String(cell))),
          );
          const headers = rows[headerRowIndex] || [];
          const datatypeColumn = headers.findIndex((cell) =>
            /expected\s*datatype/i.test(String(cell)),
          );
          const errorCountColumn = headers.findIndex((cell) =>
            /no\.\s*of\s*errors/i.test(String(cell)),
          );

          rows.slice(headerRowIndex + 1).forEach((row) => {
            const type = String(row[datatypeColumn] || "")
              .trim()
              .toLowerCase();
            const count = Number(row[errorCountColumn]);
            if (type && !Number.isNaN(count)) counts[type] = count;
          });
        });
        setPreviewErrorCounts(counts);
      } catch (_) {
        setPreviewErrorCounts({});
      }
    };

    getPreviewErrorCounts();
  }, [previewExcel]);

  useEffect(() => {
    const reportName = activeReport;
    const reportUrl = bulkReview?.reports?.[reportName];
    if (!bulkReview || !reportName || !reportUrl || reportData[reportName]) {
      return;
    }

    const fetchReport = async () => {
      try {
        setIsLoadingReport(true);
        const response = await fetch(reportUrl);
        if (!response.ok) throw new Error("Unable to fetch the Excel report");
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils
          .sheet_to_json(worksheet, { header: 1, defval: "" })
          .filter((row) => row.some((cell) => cell !== "" && cell != null));
        setReportData((data) => ({ ...data, [reportName]: rows }));
      } catch (error) {
        enqueueSnackbar(error?.message || "Unable to read the Excel report", {
          variant: "error",
        });
      } finally {
        setIsLoadingReport(false);
      }
    };

    fetchReport();
  }, [activeReport, bulkReview, enqueueSnackbar, reportData]);

  const headerIssues = bulkReview?.review?.headerMissing || [];
  const headersRequiringSelection = headerIssues.filter((item) =>
    String(item?.["Template Placeholder"] || "").trim(),
  );
  const datatypeRows = reportData.datatype_check || [];

  const handleReviewNext = () => {
    if (isCurrentReportPending) return;
    if (
      reviewStep === 0 &&
      headersRequiringSelection.some((_, index) => !headerSelections[index])
    ) {
      enqueueSnackbar(
        "Please choose an Excel header for every template placeholder",
        { variant: "error" },
      );
      return;
    }
    if (reviewStep === 1 && duplicateHeaders.length) {
      enqueueSnackbar(
        "Duplicate Excel headers found. Please reupload the Excel.",
        { variant: "error" },
      );
      return;
    }
    setReviewStep((step) => Math.min(step + 1, reviewSteps.length - 1));
  };

  /* ── Step 3: headers ok ── */
  const renderHeadersOkTable = (rows) => {
    const headerRowIndex = rows.findIndex(
      (row) =>
        row.some((column) => /template\s*placeholder/i.test(String(column))) &&
        row.some((column) => /excel\s*header/i.test(String(column))),
    );
    const columns = rows[headerRowIndex] || [];
    const values = rows.slice(headerRowIndex >= 0 ? headerRowIndex + 1 : 1);

    if (!columns.length || !values.length) {
      return (
        <EmptyState>
          <EmptyIcon>i</EmptyIcon>
          No headers are available in this report.
        </EmptyState>
      );
    }

    return (
      <>
        <ResultPill>{values.length} headers matched</ResultPill>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <Th key={index}>{column}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {values.map((row, rowIndex) => (
                <Tr key={rowIndex} even={rowIndex % 2 === 0}>
                  {columns.map((column, columnIndex) => (
                    <Td key={columnIndex} data-label={String(column)}>
                      {row[columnIndex]}
                    </Td>
                  ))}
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </>
    );
  };

  /* ── Step 4: datatypes ── */
  const renderDatatypeTable = (rows) => {
    const headerRowIndex = rows.findIndex(
      (row) =>
        row.some((column) => /^column$/i.test(String(column).trim())) &&
        row.some((column) => /excel\s*header/i.test(String(column))),
    );
    const columns = rows[headerRowIndex] || [];
    const values = rows.slice(headerRowIndex >= 0 ? headerRowIndex + 1 : 1);
    const chosenDataTypeColumn = columns.findIndex((column) =>
      /chosen\s*data\s*type/i.test(String(column)),
    );
    const excelHeaderColumn = columns.findIndex((column) =>
      /excel\s*header/i.test(String(column)),
    );
    const filteredValues = values
      .map((row, rowIndex) => ({ row, rowIndex }))
      .filter(({ row }) =>
        String(row[excelHeaderColumn] || "")
          .toLowerCase()
          .includes(excelHeaderSearch.trim().toLowerCase()),
      );

    if (!columns.length || !values.length) {
      return (
        <EmptyState>
          <EmptyIcon>i</EmptyIcon>
          No datatype values are available in this report.
        </EmptyState>
      );
    }

    const chosenCount = values.filter(
      (row, index) =>
        datatypeSelections[index] ||
        (chosenDataTypeColumn >= 0 && row[chosenDataTypeColumn]),
    ).length;

    const renderSelect = (rowIndex, currentValue) => (
      <SelectCell>
        <ModernSelect
          name={`datatypeSelection-${rowIndex}`}
          value={datatypeSelections[rowIndex] || currentValue || ""}
          options={datatypeSelectOptions}
          placeholder="Choose data type"
          onChange={(val) =>
            setDatatypeSelections((selected) => ({
              ...selected,
              [rowIndex]: val,
            }))
          }
        />
      </SelectCell>
    );

    return (
      <>
        <GuideBox>
          <GuideToggle type="button" onClick={() => setShowGuide((s) => !s)}>
            <span>Data type guide</span>
            <GuideToggleHint>
              Data type details
              <Chevron isOpen={showGuide}>▾</Chevron>
            </GuideToggleHint>
          </GuideToggle>
          {showGuide && (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Data type</Th>
                    <Th>What it means</Th>
                    <Th>Valid examples</Th>
                  </tr>
                </thead>
                <tbody>
                  {datatypeLegend
                    .filter((item) => item.type !== "COLUMN HEADER (any error)")
                    .map((item, index) => (
                      <Tr key={item.type} even={index % 2 === 0}>
                        <Td data-label="Data type">{item.type}</Td>
                        <Td data-label="What it means">{item.meaning}</Td>
                        <Td data-label="Valid examples">{item.example}</Td>
                      </Tr>
                    ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </GuideBox>

        <Toolbar>
          <ToolbarSearch>
            <SearchBar
              placeHolderText="Search Excel header"
              inputText={excelHeaderSearch}
              setInputText={setExcelHeaderSearch}
            />
          </ToolbarSearch>
          <ResultPill warn={chosenCount < values.length}>
            {chosenCount} of {values.length} chosen
          </ResultPill>
        </Toolbar>

        <TableWrap>
          <Table>
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <Th key={index}>{column}</Th>
                ))}
                {chosenDataTypeColumn < 0 && <Th>Chosen data type</Th>}
              </tr>
            </thead>
            <tbody>
              {filteredValues.length === 0 && (
                <tr>
                  <Td
                    colSpan={
                      columns.length + (chosenDataTypeColumn < 0 ? 1 : 0)
                    }
                  >
                    <EmptyState style={{ border: "none" }}>
                      No headers match your search.
                    </EmptyState>
                  </Td>
                </tr>
              )}
              {filteredValues.map(({ row, rowIndex }) => (
                <Tr key={rowIndex} even={rowIndex % 2 === 0}>
                  {columns.map((column, columnIndex) => (
                    <Td key={columnIndex} data-label={String(column)}>
                      {columnIndex === chosenDataTypeColumn
                        ? renderSelect(rowIndex, row[chosenDataTypeColumn])
                        : row[columnIndex]}
                    </Td>
                  ))}
                  {chosenDataTypeColumn < 0 && (
                    <Td data-label="Chosen data type">
                      {renderSelect(rowIndex, "")}
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </>
    );
  };

  const createDatatypeFile = () => {
    const datatypeFileRows = datatypeRows.map((row) => [...row]);
    const headerRowIndex = datatypeFileRows.findIndex(
      (row) =>
        row.some((column) => /^column$/i.test(String(column).trim())) &&
        row.some((column) => /excel\s*header/i.test(String(column))),
    );
    let chosenDataTypeColumn = datatypeFileRows[headerRowIndex]?.findIndex(
      (column) => /chosen\s*data\s*type/i.test(String(column)),
    );
    const dataStartRow = headerRowIndex + 1;

    if (headerRowIndex >= 0 && chosenDataTypeColumn < 0) {
      chosenDataTypeColumn = datatypeFileRows[headerRowIndex].length;
      datatypeFileRows[headerRowIndex].push("Chosen Data Type");
      datatypeFileRows.slice(dataStartRow).forEach((row, index) => {
        row[chosenDataTypeColumn] = datatypeSelections[index] || "";
      });
    } else if (chosenDataTypeColumn >= 0) {
      datatypeFileRows.slice(dataStartRow).forEach((row, index) => {
        if (datatypeSelections[index]) {
          row[chosenDataTypeColumn] = datatypeSelections[index];
        }
      });
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet(datatypeFileRows),
      "Data Types",
    );
    return new File(
      [XLSX.write(workbook, { bookType: "xlsx", type: "array" })],
      "datatype_check.xlsx",
      {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
    );
  };

  const handleReviewConfirm = () => {
    const headerOverrides = headerIssues.reduce((overrides, item) => {
      const selectionIndex = headersRequiringSelection.indexOf(item);
      const excelHeader = headerSelections[selectionIndex];
      if (excelHeader) {
        overrides[item?.["Template Placeholder"]] = excelHeader;
      }
      return overrides;
    }, {});

    if (onReviewConfirm) {
      onReviewConfirm({
        datatypeFile: createDatatypeFile(),
        headerOverrides,
      });
    }
  };

  /* ── Step 5: null values ── */
  const renderNullValuesTable = (rows) => {
    const headerRowIndex = rows.findIndex(
      (row) =>
        row.some((column) => /column\s*letter/i.test(String(column))) &&
        row.some((column) => /column\s*name/i.test(String(column))) &&
        row.some((column) => /empty\s*rows/i.test(String(column))),
    );
    const columns = rows[headerRowIndex] || [];
    const values = rows.slice(headerRowIndex >= 0 ? headerRowIndex + 1 : 1);
    const emptyRowsColumn = columns.findIndex((column) =>
      /empty\s*rows/i.test(String(column)),
    );

    if (!columns.length || !values.length) {
      return (
        <EmptyState>
          <EmptyIcon ok>✓</EmptyIcon>
          No empty values are available in this report.
        </EmptyState>
      );
    }

    const cellText = (row) => String(row[emptyRowsColumn] || "").trim();
    const isAllEmpty = (row) => /^all rows are empty$/i.test(cellText(row));
    const isSomeEmpty = (row) => !isAllEmpty(row) && cellText(row) !== "";

    const filterOptions = [
      {
        label: "All rows are empty",
        value: "allRowsEmpty",
        count: values.filter(isAllEmpty).length,
      },
      {
        label: "Some rows are empty",
        value: "nullRows",
        count: values.filter(isSomeEmpty).length,
      },
    ];

    const filteredValues = values.filter((row) =>
      isAllEmpty(row)
        ? emptyRowsFilter.includes("allRowsEmpty")
        : isSomeEmpty(row) && emptyRowsFilter.includes("nullRows"),
    );

    return (
      <>
        <InfoNote>
          <b>How to read this:</b> if a column shows <b>All Rows Are Empty</b>,
          every row in that column is empty. For other columns, the row numbers
          that have empty cells are listed.
        </InfoNote>

        <FilterRow>
          <FilterLabel>Show</FilterLabel>
          {filterOptions.map((item) => {
            const on = emptyRowsFilter.includes(item.value);
            return (
              <FilterChip
                key={item.value}
                type="button"
                isOn={on}
                aria-pressed={on}
                onClick={() =>
                  setEmptyRowsFilter((selected) =>
                    selected.includes(item.value)
                      ? selected.filter((v) => v !== item.value)
                      : [...selected, item.value],
                  )
                }
              >
                {on && <span aria-hidden="true">✓</span>}
                {item.label}
                <FilterCount isOn={on}>{item.count}</FilterCount>
              </FilterChip>
            );
          })}
        </FilterRow>

        <TableWrap>
          <Table>
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <Th key={index}>{column}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredValues.length === 0 && (
                <tr>
                  <Td colSpan={columns.length}>
                    <EmptyState style={{ border: "none" }}>
                      Nothing to show with the current filter.
                    </EmptyState>
                  </Td>
                </tr>
              )}
              {filteredValues.map((row, rowIndex) => (
                <Tr key={rowIndex} even={rowIndex % 2 === 0}>
                  {columns.map((column, columnIndex) => (
                    <Td key={columnIndex} data-label={String(column)}>
                      {columnIndex === emptyRowsColumn && isAllEmpty(row) ? (
                        <WarnBadge>{row[columnIndex]}</WarnBadge>
                      ) : (
                        row[columnIndex]
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </>
    );
  };

  /* ── Step 1: header issues ── */
  const renderHeaderIssues = () => {
    if (!headerIssues.length) {
      return (
        <EmptyState>
          <EmptyIcon ok>✓</EmptyIcon>
          No header is missing in the template.
        </EmptyState>
      );
    }

    const total = headersRequiringSelection.length;
    const chosen = headersRequiringSelection.filter(
      (_, i) => headerSelections[i],
    ).length;
    const headerOptions = excelHeaders.map((header) => ({
      value: header,
      label: header,
    }));

    return (
      <>
        {total > 0 && (
          <ResultPill warn={chosen < total}>
            {chosen} of {total} chosen
          </ResultPill>
        )}
        <MapList>
          {headerIssues.map((item, index) => {
            const placeholder = String(
              item?.["Template Placeholder"] || "",
            ).trim();
            const suggestedHeader = headerSuggestions.find(
              (suggestion) =>
                String(suggestion?.["Template Placeholder"] || "").trim() ===
                placeholder,
            )?.["Suggested Excel Header"];
            const suggestedHeaders = (
              Array.isArray(suggestedHeader)
                ? suggestedHeader
                : [suggestedHeader]
            ).filter(
              (header) =>
                header &&
                !/^no\s+suggested\s+excel\s+header$/i.test(
                  String(header).trim(),
                ),
            );
            const selectionIndex = headersRequiringSelection.indexOf(item);
            const picked = placeholder && headerSelections[selectionIndex];
            return (
              <MapRow key={index} isDone={Boolean(picked)}>
                <MapSide>
                  <MapLabel>Template placeholder</MapLabel>
                  <CodeChip>{item?.["Template Placeholder"] || "-"}</CodeChip>
                </MapSide>
                <MapArrow aria-hidden="true">→</MapArrow>
                <MapSide>
                  <MapLabel>Excel header</MapLabel>
                  {placeholder ? (
                    <ModernSelect
                      name={`headerSelection-${selectionIndex}`}
                      searchable
                      value={headerSelections[selectionIndex] || ""}
                      options={[
                        {
                          value: "suggestedExcelHeader",
                          label: "Suggested Excel Header",
                          disabled: true,
                          isGroup: true,
                        },
                        ...(suggestedHeaders.length
                          ? suggestedHeaders.map((header) => ({
                              value: header,
                              label: header,
                            }))
                          : [
                              {
                                value: null,
                                label: "No Suggested Excel Header",
                                disabled: true,
                              },
                            ]),
                        {
                          value: "excelHeader",
                          label: "Excel Header",
                          disabled: true,
                          isGroup: true,
                        },
                        ...headerOptions.filter(
                          (option) => !suggestedHeaders.includes(option.value),
                        ),
                      ]}
                      placeholder="Choose Excel header"
                      onChange={(val) =>
                        setHeaderSelections((selected) => ({
                          ...selected,
                          [selectionIndex]: val,
                        }))
                      }
                    />
                  ) : (
                    <Muted>Nothing to choose</Muted>
                  )}
                </MapSide>
              </MapRow>
            );
          })}
        </MapList>
      </>
    );
  };

  /* ── Step 2: duplicates ── */
  const renderDuplicates = () =>
    duplicateHeaders.length ? (
      <>
        <ResultPill warn>{duplicateHeaders.length} duplicates found</ResultPill>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                {typeof duplicateHeaders[0] === "object" ? (
                  Object.keys(duplicateHeaders[0]).map((column) => (
                    <Th key={column}>{column}</Th>
                  ))
                ) : (
                  <Th>Duplicate Excel header</Th>
                )}
              </tr>
            </thead>
            <tbody>
              {duplicateHeaders.map((header, index) => (
                <Tr key={index} even={index % 2 === 0}>
                  {typeof header === "object" ? (
                    Object.keys(duplicateHeaders[0]).map((column) => (
                      <Td key={column} data-label={column}>
                        {header?.[column]}
                      </Td>
                    ))
                  ) : (
                    <Td data-label="Duplicate Excel header">{header}</Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </>
    ) : (
      <EmptyState>
        <EmptyIcon ok>✓</EmptyIcon>
        No duplicate headers found.
      </EmptyState>
    );

  const renderBulkReview = () => {
    if (reviewStep === 0) return renderHeaderIssues();
    if (reviewStep === 1) return renderDuplicates();
    if (reviewStep === 2)
      return renderHeadersOkTable(reportData.headers_ok || []);
    if (reviewStep === 3) return renderDatatypeTable(datatypeRows);
    return renderNullValuesTable(reportData.null_values || []);
  };

  const isLastStep = reviewStep === reviewSteps.length - 1;
  const footerWarn =
    (reviewStep === 0 &&
      headersRequiringSelection.some((_, i) => !headerSelections[i])) ||
    (reviewStep === 1 && duplicateHeaders.length > 0);
  const footerHint =
    reviewStep === 0 && footerWarn
      ? "Choose a header for each placeholder to continue"
      : reviewStep === 1 && footerWarn
        ? "Fix the duplicates and upload the Excel again"
        : ``;

  /* ────────────────────────────────────────────────────────────
     Render
  ──────────────────────────────────────────────────────────── */
  return (
    <Fragment>
      <Modal open={open} onClose={onClose} disableEnforceFocus>
        <Overlay>
          <ModalCard>
            {/* Header */}
            <ModalHeader>
              <IconBadge>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 11 12 14 22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              </IconBadge>
              <ModalTitle>
                {bulkReview
                  ? title || "Review your Excel"
                  : title || "Assignment summary"}
              </ModalTitle>
              <CloseBtn onClick={onClose} aria-label="Close">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ModalHeader>

            {/* Stepper */}
            {bulkReview && (
              <StepperWrap>
                <StepperTrack>
                  {reviewSteps.map((s, i) => {
                    const state =
                      i === reviewStep
                        ? "active"
                        : i < reviewStep
                          ? "done"
                          : "todo";
                    return (
                      <StepItem
                        key={s.step}
                        type="button"
                        disabled={i >= reviewStep}
                        aria-current={i === reviewStep ? "step" : undefined}
                        onClick={() => i < reviewStep && setReviewStep(i)}
                      >
                        <StepDot state={state}>
                          {i < reviewStep ? "✓" : s.step}
                        </StepDot>
                        <StepName isActive={i === reviewStep}>
                          {s.short}
                        </StepName>
                      </StepItem>
                    );
                  })}
                </StepperTrack>
                <ProgressBar>
                  <ProgressFill
                    style={{
                      width: `${((reviewStep + 1) / reviewSteps.length) * 100}%`,
                    }}
                  />
                </ProgressBar>
              </StepperWrap>
            )}

            {/* Body */}
            <ModalBody>
              {bulkReview ? (
                <Section key={reviewStep}>
                  <StepIntro>
                    <StepTitle>
                      Step {activeReviewStep.step} of {reviewSteps.length}:{" "}
                      {activeReviewStep.title}
                    </StepTitle>
                    <StepHelp>{activeReviewStep.help}</StepHelp>
                  </StepIntro>
                  {isCurrentReportPending || isLoadingReport ? (
                    <LoadingBox>
                      <Spinner />
                      Loading Excel report…
                    </LoadingBox>
                  ) : (
                    renderBulkReview()
                  )}
                </Section>
              ) : datatypeErrors ? (
                <Section>
                  <StepIntro>
                    <StepTitle>Data type errors found</StepTitle>
                    <StepHelp>
                      Each colour below matches the colour used in your error
                      file. Download the file to see exactly which cells to fix.
                    </StepHelp>
                  </StepIntro>
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <Th style={{ width: 56 }}>Colour</Th>
                          <Th>Expected data type</Th>
                          <Th>What it means</Th>
                          <Th>Valid examples</Th>
                          <Th style={{ textAlign: "center" }}>Errors</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {datatypeLegend
                          .filter((item) => item.type !== "string")
                          .map((item, index) => {
                            const count =
                              item.count || datatypeErrorCount(item.type);
                            return (
                              <Tr key={item.type} even={index % 2 === 0}>
                                <Td data-label="Colour">
                                  <ColorDot tone={item.color} />
                                </Td>
                                <Td data-label="Expected data type">
                                  <strong>{item.type}</strong>
                                </Td>
                                <Td data-label="What it means">
                                  {item.meaning}
                                </Td>
                                <Td data-label="Valid examples">
                                  {item.example}
                                </Td>
                                <Td
                                  data-label="Errors"
                                  style={{ textAlign: "center" }}
                                >
                                  {Number(count) > 0 ? (
                                    <ErrorCount>{count}</ErrorCount>
                                  ) : (
                                    count
                                  )}
                                </Td>
                              </Tr>
                            );
                          })}
                      </tbody>
                    </Table>
                  </TableWrap>
                  {errorExcel && (
                    <DownloadBtn type="button" onClick={downloadErrorExcel}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download error file
                    </DownloadBtn>
                  )}
                </Section>
              ) : (
                responseData && (
                  <>
                    <StatsGrid>
                      <StatCard variant="neutral">
                        <StatValue>{responseData.totalCasesCount}</StatValue>
                        <StatLabel>Total cases</StatLabel>
                      </StatCard>
                      <StatCard variant="success">
                        <StatValue variant="success">
                          {responseData.updatedCasesCount}
                        </StatValue>
                        <StatLabel>Updated</StatLabel>
                      </StatCard>
                      <StatCard variant="warning">
                        <StatValue variant="warning">
                          {responseData.declinedCasesCount || 0}
                        </StatValue>
                        <StatLabel>Declined</StatLabel>
                      </StatCard>
                    </StatsGrid>

                    <SuccessBanner>
                      <BannerCheck>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </BannerCheck>
                      <BannerText>{responseData.message}</BannerText>
                    </SuccessBanner>

                    {responseData.declinedCases?.length > 0 && (
                      <Section>
                        <SectionHeader>
                          <SectionTitleRow>
                            <SectionDot variant="warning" />
                            <SectionTitle>Declined cases</SectionTitle>
                            <CountPill variant="warning">
                              {responseData.declinedCases.length}
                            </CountPill>
                          </SectionTitleRow>
                          <CopyBtn type="button" onClick={handleCopyTable}>
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect
                                x="9"
                                y="9"
                                width="13"
                                height="13"
                                rx="2"
                                ry="2"
                              />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                            Copy table
                          </CopyBtn>
                        </SectionHeader>

                        <TableWrap>
                          <Table>
                            <thead>
                              <tr>
                                <Th>Case ID</Th>
                                <Th>Resolution</Th>
                                <Th>Reason</Th>
                                <Th>Status</Th>
                              </tr>
                            </thead>
                            <tbody>
                              {responseData.declinedCases.map((c, i) => (
                                <Tr key={i} even={i % 2 === 0}>
                                  <Td data-label="Case ID">
                                    <CaseIdChip>{c.caseId}</CaseIdChip>
                                  </Td>
                                  <Td data-label="Resolution">
                                    {c.resolutionKind}
                                  </Td>
                                  <Td data-label="Reason">
                                    <ReasonBadge>{c.reason}</ReasonBadge>
                                  </Td>
                                  <Td data-label="Status">
                                    <StatusBadge status={c.status}>
                                      {c.status}
                                    </StatusBadge>
                                  </Td>
                                </Tr>
                              ))}
                            </tbody>
                          </Table>
                        </TableWrap>
                      </Section>
                    )}

                    {responseData.allowedCaseIds?.length > 0 &&
                      responseData.updatedCasesCount > 0 && (
                        <Section>
                          <SectionHeader>
                            <SectionTitleRow>
                              <SectionDot variant="success" />
                              <SectionTitle>Updated cases</SectionTitle>
                              <CountPill variant="success">
                                {responseData.allowedCaseIds.length}
                              </CountPill>
                            </SectionTitleRow>
                          </SectionHeader>
                          <TagCloud>
                            {responseData.allowedCaseIds.map((id, i) => (
                              <SuccessTag key={i}>{id}</SuccessTag>
                            ))}
                          </TagCloud>
                        </Section>
                      )}
                  </>
                )
              )}
            </ModalBody>

            {/* Sticky footer */}
            {bulkReview && (
              <ModalFooter>
                <PrimaryOutlinedCTAButton
                  type="button"
                  onClick={() =>
                    reviewStep === 0
                      ? onClose()
                      : setReviewStep((step) => Math.max(step - 1, 0))
                  }
                >
                  {reviewStep === 0 ? "Cancel" : "Back"}
                </PrimaryOutlinedCTAButton>
                <FooterInfo isWarn={footerWarn}>{footerHint}</FooterInfo>
                {isLastStep ? (
                  <PrimaryCTAButton type="button" onClick={handleReviewConfirm}>
                    Confirm
                  </PrimaryCTAButton>
                ) : (
                  <PrimaryCTAButton
                    type="button"
                    disabled={isCurrentReportPending || isLoadingReport}
                    onClick={handleReviewNext}
                  >
                    Next
                  </PrimaryCTAButton>
                )}
              </ModalFooter>
            )}
          </ModalCard>
        </Overlay>
      </Modal>
    </Fragment>
  );
};

export default ResponseModal;

/* ────────────────────────────────────────────────────────────────
   Styles
──────────────────────────────────────────────────────────────── */
const fadeIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%); }
  to   { opacity: 1; transform: translate(-50%, -50%); }
`;

const sheetUp = keyframes`
  from { transform: translateY(40px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
`;

const fade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  outline: none;
`;

const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 32px);
  width: 60%;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.25);
  outline: none;
  animation: ${fadeIn} 0.22s ease-out both;
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  overflow: hidden;

  @media (max-width: 600px) {
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    max-width: 100%;
    border-radius: 20px 20px 0 0;
    max-height: 94vh;
    animation: ${sheetUp} 0.25s ease-out both;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 24px;
  border-bottom: 1px solid #eef0f3;
  position: relative;
  flex-shrink: 0;

  @media (max-width: 600px) {
    padding: 14px 16px;
  }
`;

const IconBadge = styled.div`
  width: 38px;
  height: 38px;
  background: ${COLORS.BTN_GREEN}18;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.BTN_GREEN};
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
  letter-spacing: -0.2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 600px) {
    font-size: 16px;
  }
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: #f3f4f6;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition:
    background 0.18s,
    color 0.18s;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    color: #ef4444;
  }
  &:focus-visible {
    outline: 2px solid ${COLORS.BTN_GREEN};
    outline-offset: 2px;
  }
`;

/* Stepper */
const StepperWrap = styled.div`
  padding: 14px 24px 0;
  flex-shrink: 0;
  background: #fafbfc;
  border-bottom: 1px solid #eef0f3;

  @media (max-width: 600px) {
    padding: 12px 16px 0;
  }
`;

const StepperTrack = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 4px;
`;

const StepItem = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  &:disabled {
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${COLORS.BTN_GREEN};
    outline-offset: 2px;
    border-radius: 8px;
  }
`;

const stepColors = {
  active: { bg: COLORS.BTN_GREEN, fg: "#fff", ring: `${COLORS.BTN_GREEN}33` },
  done: { bg: COLORS.BTN_GREEN, fg: "#fff", ring: "transparent" },
  todo: { bg: "#e5e7eb", fg: "#6b7280", ring: "transparent" },
};

const StepDot = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  font-family: ${theme.fonts.primaryFontSemiBold};
  background: ${(p) => stepColors[p.state].bg};
  color: ${(p) => stepColors[p.state].fg};
  box-shadow: 0 0 0 4px ${(p) => stepColors[p.state].ring};
  transition: background 0.2s;
`;

const StepName = styled.span`
  font-size: 12px;
  line-height: 1.2;
  text-align: center;
  color: ${(p) => (p.isActive ? "#111827" : "#6b7280")};
  font-weight: ${(p) => (p.isActive ? 700 : 500)};
  font-family: ${theme.fonts.primaryFontSemiBold};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 600px) {
    display: ${(p) => (p.isActive ? "block" : "none")};
    white-space: normal;
  }
`;

const ProgressBar = styled.div`
  height: 3px;
  background: #e5e7eb;
  margin: 12px -24px 0;

  @media (max-width: 600px) {
    margin: 10px -16px 0;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, ${COLORS.BTN_GREEN});
  transition: width 0.3s ease;
`;

/* Body + footer */
const ModalBody = styled.div`
  overflow-y: auto;
  padding: 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 99px;
  }
  @media (max-width: 600px) {
    padding: 16px;
    gap: 16px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 24px;
  border-top: 1px solid #eef0f3;
  background: #fff;
  flex-shrink: 0;

  @media (max-width: 600px) {
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  }
`;

const FooterInfo = styled.span`
  flex: 1;
  text-align: center;
  font-size: 13px;
  font-weight: ${(p) => (p.isWarn ? 600 : 400)};
  color: ${(p) => (p.isWarn ? "#c2410c" : "#6b7280")};
  font-family: ${theme.fonts.primaryFontRegular};
  @media (max-width: 480px) {
    display: none;
  }
`;

/* Step intro */
const StepIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StepTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const StepHelp = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #6b7280;
  max-width: 70ch;
  font-family: ${theme.fonts.primaryFontRegular};
`;

/* Stats */
const variantMap = {
  neutral: { bg: "#f4f6f8", border: "#e5e7eb", val: "#1a2035" },
  success: {
    bg: `${COLORS.BTN_GREEN}0d`,
    border: `${COLORS.BTN_GREEN}30`,
    val: COLORS.BTN_GREEN,
  },
  warning: { bg: "#fff7ed", border: "#fed7aa", val: "#ea580c" },
};

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media (max-width: 400px) {
    gap: 8px;
  }
`;

const StatCard = styled.div`
  background: ${(p) => variantMap[p.variant]?.bg};
  border: 1.5px solid ${(p) => variantMap[p.variant]?.border};
  border-radius: 14px;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 800;
  font-family: ${theme.fonts.primaryFontExtraBold};
  color: ${(p) => variantMap[p.variant]?.val || "#1a2035"};
  line-height: 1;
  @media (max-width: 400px) {
    font-size: 22px;
  }
`;

const StatLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
`;

const BannerCheck = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${COLORS.BTN_GREEN};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const BannerText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #166534;
  line-height: 1.4;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

/* Sections */
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: ${fade} 0.25s ease both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) =>
    p.variant === "success" ? COLORS.BTN_GREEN : "#f97316"};
  flex-shrink: 0;
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const CountPill = styled.span`
  font-size: 12px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 99px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  background: ${(p) =>
    p.variant === "success" ? `${COLORS.BTN_GREEN}15` : "#fff7ed"};
  color: ${(p) => (p.variant === "success" ? COLORS.BTN_GREEN : "#ea580c")};
  border: 1px solid
    ${(p) => (p.variant === "success" ? `${COLORS.BTN_GREEN}30` : "#fed7aa")};
`;

const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${COLORS.BTN_GREEN};
  color: #fff;
  border: none;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: pointer;
  transition:
    transform 0.15s,
    box-shadow 0.15s;
  box-shadow: 0 2px 6px ${COLORS.BTN_GREEN}40;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${COLORS.BTN_GREEN}50;
  }
  &:active {
    transform: translateY(0);
  }
  &:focus-visible {
    outline: 2px solid ${COLORS.BTN_GREEN};
    outline-offset: 2px;
  }
`;

const DownloadBtn = styled(CopyBtn)`
  align-self: flex-end;
  padding: 10px 16px;
  @media (max-width: 480px) {
    align-self: stretch;
    justify-content: center;
  }
`;

/* Pills / notes / empty */
const ResultPill = styled.span`
  align-self: flex-start;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 99px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  background: ${(p) => (p.warn ? "#fff7ed" : `${COLORS.BTN_GREEN}12`)};
  color: ${(p) => (p.warn ? "#c2410c" : COLORS.BTN_GREEN)};
  border: 1px solid ${(p) => (p.warn ? "#fed7aa" : `${COLORS.BTN_GREEN}30`)};
  white-space: nowrap;
`;

const InfoNote = styled.div`
  font-size: 13px;
  line-height: 1.55;
  color: #1e3a8a;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 12px 16px;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 28px 16px;
  font-size: 14px;
  color: #4b5563;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 14px;
  text-align: center;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const EmptyIcon = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  background: ${(p) => (p.ok ? COLORS.BTN_GREEN : "#6b7280")};
  flex-shrink: 0;
`;

const LoadingBox = styled(EmptyState)`
  padding: 48px 16px;
`;

const Spinner = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 3px solid #e5e7eb;
  border-top-color: ${COLORS.BTN_GREEN};
  animation: ${spin} 0.8s linear infinite;
`;

const Muted = styled.span`
  font-size: 13px;
  color: #9ca3af;
  font-family: ${theme.fonts.primaryFontRegular};
`;

/* Step 1: mapping rows */
const MapList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MapRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: end;
  gap: 16px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid ${(p) => (p.isDone ? `${COLORS.BTN_GREEN}55` : "#e5e7eb")};
  border-left: 4px solid ${(p) => (p.isDone ? COLORS.BTN_GREEN : "#fdba74")};
  border-radius: 12px;
  transition: border-color 0.2s;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const MapSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const MapLabel = styled.span`
  font-size: 12px;
  color: #6b7280;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const MapArrow = styled.span`
  color: #9ca3af;
  font-size: 18px;
  padding-bottom: 8px;
  @media (max-width: 600px) {
    display: none;
  }
`;

/* Guide (datatype step) */
const GuideBox = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fafbfc;
`;

const GuideToggle = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  font-family: ${theme.fonts.primaryFontSemiBold};
  &:focus-visible {
    outline: 2px solid ${COLORS.BTN_GREEN};
    outline-offset: -2px;
  }
`;

const GuideToggleHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  @media (max-width: 480px) {
    font-size: 0;
    gap: 0;
  }
`;

const Chevron = styled.span`
  display: inline-block;
  font-size: 14px;
  transition: transform 0.2s;
  transform: rotate(${(p) => (p.isOpen ? "180deg" : "0deg")});
  color: #6b7280;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const ToolbarSearch = styled.div`
  flex: 1 1 240px;
  min-width: 0;
`;

/* Step 5: filter chips */
const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  font-size: 13px;
  color: #6b7280;
  margin-right: 2px;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 99px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${(p) => (p.isOn ? COLORS.BTN_GREEN : "#4b5563")};
  background: ${(p) => (p.isOn ? `${COLORS.BTN_GREEN}12` : "#fff")};
  border: 1px solid ${(p) => (p.isOn ? `${COLORS.BTN_GREEN}66` : "#d1d5db")};
  transition:
    background 0.15s,
    border-color 0.15s;
  &:hover {
    border-color: ${COLORS.BTN_GREEN};
  }
  &:focus-visible {
    outline: 2px solid ${COLORS.BTN_GREEN};
    outline-offset: 2px;
  }
`;

const FilterCount = styled.span`
  min-width: 22px;
  padding: 1px 7px;
  border-radius: 99px;
  font-size: 11px;
  text-align: center;
  color: ${(p) => (p.isOn ? "#fff" : "#4b5563")};
  background: ${(p) => (p.isOn ? COLORS.BTN_GREEN : "#e5e7eb")};
`;

/* Tables (stack into cards on small screens) */
const TableWrap = styled.div`
  overflow: auto;
  max-height: 52vh;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    height: 6px;
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 99px;
  }
  @media (max-width: 600px) {
    max-height: none;
    border: none;
    overflow: visible;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 520px;

  @media (max-width: 600px) {
    min-width: 0;
    display: block;
    thead {
      display: none;
    }
    tbody {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
  }
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #4b5563;
  background: #f3f4f6;
  font-family: ${theme.fonts.primaryFontSemiBold};
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Tr = styled.tr`
  background: ${(p) => (p.even ? "#fff" : "#fafafa")};
  transition: background 0.15s;
  &:hover {
    background: #f0fdf4;
  }
  & > td {
    border-bottom: 1px solid #f3f4f6;
  }
  &:last-child > td {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    display: block;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 4px 0;
    & > td {
      border-bottom: none;
    }
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.45;
  color: #374151;
  vertical-align: middle;
  word-break: break-word;
  font-family: ${theme.fonts.primaryFontRegular};

  @media (max-width: 600px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 14px;
    text-align: right;
    &[data-label]::before {
      content: attr(data-label);
      flex-shrink: 0;
      max-width: 42%;
      text-align: left;
      font-size: 12px;
      font-weight: 600;
      color: #6b7280;
    }
  }
`;

const SelectCell = styled.div`
  width: 100%;
  min-width: 180px;
  max-width: 260px;
  text-align: left;
  @media (max-width: 600px) {
    min-width: 0;
    flex: 1;
  }
`;

const CodeChip = styled.span`
  display: inline-block;
  align-self: flex-start;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  padding: 6px 10px;
  border-radius: 8px;
  word-break: break-all;
`;

const ColorDot = styled.span`
  display: block;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: ${(p) => p.tone};
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
`;

const ErrorCount = styled.span`
  display: inline-block;
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 700;
  color: #b91c1c;
  background: #fee2e2;
`;

const WarnBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fde68a;
`;

const CaseIdChip = styled.span`
  font-weight: 700;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #1a2035;
  background: #f3f4f6;
  padding: 3px 8px;
  border-radius: 6px;
  white-space: nowrap;
`;

const ReasonBadge = styled.span`
  display: inline-block;
  background: #fefce8;
  color: #854d0e;
  border: 1px solid #fde68a;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: ${theme.fonts.primaryFontSemiBold};
  white-space: nowrap;
  background: ${(p) => {
    switch (p.status) {
      case "proformaGenerated":
        return "#eff6ff";
      case "pendingRegistrationFee":
        return "#fdf2f8";
      default:
        return "#f3f4f6";
    }
  }};
  color: ${(p) => {
    switch (p.status) {
      case "proformaGenerated":
        return "#1d4ed8";
      case "pendingRegistrationFee":
        return "#9d174d";
      default:
        return "#4b5563";
    }
  }};
  border: 1px solid
    ${(p) => {
      switch (p.status) {
        case "proformaGenerated":
          return "#bfdbfe";
        case "pendingRegistrationFee":
          return "#fbcfe8";
        default:
          return "#e5e7eb";
      }
    }};
`;

const TagCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SuccessTag = styled.span`
  background: ${COLORS.BTN_GREEN}14;
  color: ${COLORS.BTN_GREEN};
  border: 1px solid ${COLORS.BTN_GREEN}40;
  padding: 5px 12px;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 700;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

/* ── Modern select + tooltip ── */
const SelectRoot = styled.div`
  position: relative;
  width: 100%;
`;

const SelectTrigger = styled.button`
  width: 100%;
  min-height: 40px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  text-align: left;
  cursor: pointer;
  background: #fff;
  border: 1px solid
    ${(p) => (p.isOpen ? COLORS.BTN_GREEN : p.isFilled ? "#cbd5e1" : "#d1d5db")};
  box-shadow: ${(p) => (p.isOpen ? `0 0 0 3px ${COLORS.BTN_GREEN}22` : "none")};
  border-radius: 10px;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  &:hover {
    border-color: ${COLORS.BTN_GREEN};
  }
  &:focus-visible {
    outline: none;
    border-color: ${COLORS.BTN_GREEN};
    box-shadow: 0 0 0 3px ${COLORS.BTN_GREEN}33;
  }
`;

const TriggerText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: ${(p) => (p.isPlaceholder ? 400 : 600)};
  color: ${(p) => (p.isPlaceholder ? "#9ca3af" : "#111827")};
  font-family: ${theme.fonts.primaryFontRegular};
`;

const TipDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${(p) => p.tone};
  flex-shrink: 0;
`;

const MenuPanel = styled.div`
  position: fixed;
  z-index: 1600;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow:
    0 16px 40px rgba(15, 23, 42, 0.18),
    0 2px 8px rgba(15, 23, 42, 0.08);
  overflow: hidden;
`;

const MenuSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f2f5;
  flex-shrink: 0;
`;

const MenuSearchIcon = styled.span`
  color: #9ca3af;
  font-size: 16px;
`;

const MenuSearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #111827;
  font-family: ${theme.fonts.primaryFontRegular};
  &::placeholder {
    color: #9ca3af;
  }
`;

const MenuList = styled.div`
  padding: 6px;
  overflow-y: auto;
  flex: 1;
  scrollbar-width: thin;
`;

const MenuOption = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
  padding: 0 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontRegular};
  color: ${(p) => (p.isSelected ? COLORS.BTN_GREEN : "#111827")};
  font-weight: ${(p) => (p.isSelected ? 600 : 400)};
  background: ${(p) =>
    p.isSelected
      ? `${COLORS.BTN_GREEN}14`
      : p.isActive
        ? "#f3f4f6"
        : "transparent"};
`;

const OptionLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OptionHint = styled.span`
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
`;

const OptionCheck = styled.span`
  font-size: 14px;
  font-weight: 700;
`;

const MenuEmpty = styled.div`
  padding: 18px 10px;
  text-align: center;
  font-size: 13px;
  color: #6b7280;
`;

const TipBox = styled.div`
  position: fixed;
  z-index: 1700;
  width: 250px;
  max-width: calc(100vw - 16px);
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
  pointer-events: none;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const TipArrow = styled.span`
  position: absolute;
  width: 10px;
  height: 10px;
  background: #fff;
  transform: rotate(45deg);
`;

const TipHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const TipType = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const TipText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: #4b5563;
  margin-bottom: 10px;
`;

const TipLabel = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 4px;
`;

const TipExample = styled.div`
  font-size: 12.5px;
  line-height: 1.45;
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 7px 9px;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
`;
